import { atom, useAtom } from "jotai";
import { useCallback } from "react";

export enum HelpArticle {
    DESIGNS_OVERVIEW = "DESIGNS_OVERVIEW",
    PATTERN_DETAILS = "PATTERN_DETAILS",
    PATTERN_TYPES = "PATTERN_TYPES",
    MURAL_LIMITATIONS = "MURAL_LIMITATIONS",
    TECHNICAL_SPECS = "TECHNICAL_SPECS",
    DEFAULT_SCALES = "DEFAULT_SCALES",
    REVIEW_AND_NAME = "REVIEW_AND_NAME",
    COLORS_AND_STYLE = "COLORS_AND_STYLE",
    PRODUCT_SETUP = "PRODUCT_SETUP",
    WALLPAPER_SCALE = "WALLPAPER_SCALE",
    WRAPPING_PAPER_SCALE = "WRAPPING_PAPER_SCALE",
    COLLECTIONS = "COLLECTIONS",
    PHOTO_GENERATION = "PHOTO_GENERATION",
    SAMPLE_PHOTOS = "SAMPLE_PHOTOS",
    MOCKUP_PHOTOS = "MOCKUP_PHOTOS",
    LISTING_SETUP = "LISTING_SETUP",
    PRICING = "PRICING",
    LISTING_IMAGES = "LISTING_IMAGES",
    RELEASE = "RELEASE",
    DESIGNS_OVERVIEW_DETAILED = "DESIGNS_OVERVIEW_DETAILED",
    PATTERN_DETAILS_DETAILED = "PATTERN_DETAILS_DETAILED",
    PATTERN_TYPES_DETAILED = "PATTERN_TYPES_DETAILED",
    TECHNICAL_SPECS_DETAILED = "TECHNICAL_SPECS_DETAILED",
    DEFAULT_SCALES_DETAILED = "DEFAULT_SCALES_DETAILED",
    REVIEW_AND_NAME_DETAILED = "REVIEW_AND_NAME_DETAILED",
    COLORS_AND_STYLE_DETAILED = "COLORS_AND_STYLE_DETAILED",
    PRODUCT_SETUP_DETAILED = "PRODUCT_SETUP_DETAILED",
    WALLPAPER_SCALE_DETAILED = "WALLPAPER_SCALE_DETAILED",
    WRAPPING_PAPER_SCALE_DETAILED = "WRAPPING_PAPER_SCALE_DETAILED",
    COLLECTIONS_DETAILED = "COLLECTIONS_DETAILED",
    PHOTO_GENERATION_DETAILED = "PHOTO_GENERATION_DETAILED",
    SAMPLE_PHOTOS_DETAILED = "SAMPLE_PHOTOS_DETAILED",
    MOCKUP_PHOTOS_DETAILED = "MOCKUP_PHOTOS_DETAILED",
    LISTING_SETUP_DETAILED = "LISTING_SETUP_DETAILED",
    PRICING_DETAILED = "PRICING_DETAILED",
    LISTING_IMAGES_DETAILED = "LISTING_IMAGES_DETAILED",
    RELEASE_DETAILED = "RELEASE_DETAILED",
    API_ORDERS = "API_ORDERS",
    API_SAMPLE_SPEC = "API_SAMPLE_SPEC",
    // Material Guide. These same articles back the /material-guide page, so the
    // drawer and the page never drift apart.
    MATERIALS_OVERVIEW = "MATERIALS_OVERVIEW",
    MATERIAL_PEEL_AND_STICK = "MATERIAL_PEEL_AND_STICK",
    MATERIAL_LUXE_TRADITIONAL = "MATERIAL_LUXE_TRADITIONAL",
    MATERIAL_LUXE_PEEL_AND_STICK = "MATERIAL_LUXE_PEEL_AND_STICK",
    // Settings → Storefront Plugin (install, customize, troubleshooting).
    SHOPIFY_PLUGIN = "SHOPIFY_PLUGIN",
    // Settings → Shipping Rates (Shopify carrier service setup, pricing, troubleshooting).
    SHIPPING_RATES = "SHIPPING_RATES",
}

interface HelpPanelState {
    open: boolean;
    article: HelpArticle;
    history: HelpArticle[];
}

const helpPanelAtom = atom<HelpPanelState>({
    open: false,
    article: HelpArticle.DESIGNS_OVERVIEW,
    history: [],
});

export { helpPanelAtom };

export const useHelpPanel = () => {
    const [helpPanel, setHelpPanel] = useAtom(helpPanelAtom);

    const openHelp = useCallback(
        (article: HelpArticle) => {
            setHelpPanel((prev) => ({
                open: true,
                article,
                history: prev.open ? [...prev.history, prev.article] : [],
            }));
        },
        [setHelpPanel]
    );

    const closeHelp = useCallback(() => {
        setHelpPanel((prev) => ({ ...prev, open: false, history: [] }));
    }, [setHelpPanel]);

    const goBack = useCallback(() => {
        setHelpPanel((prev) => {
            if (prev.history.length === 0) return prev;
            const history = [...prev.history];
            const article = history.pop() as HelpArticle;
            return { ...prev, article, history };
        });
    }, [setHelpPanel]);

    const canGoBack = helpPanel.history.length > 0;

    return { helpPanel, openHelp, closeHelp, goBack, canGoBack };
};
